import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Heart, Trophy, Target, Lock, CheckCircle2, Sparkles } from "lucide-react";

type Rep = {
  id: string;
  creator_id: string;
  fan_id: string;
  support_total_cents: number;
  loyalty_score: number;
  missions_completed: number;
  badges: any;
  updated_at: string;
};

type Circle = {
  id: string;
  creator_id: string;
  name: string;
  circle_type: string;
  min_support_cents: number;
  min_loyalty_score: number;
  perks: any;
};

type Profile = { user_id: string; display_name: string; cashtag: string; avatar_url: string | null };

const fmtUSD = (cents: number) =>
  `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MyReputation() {
  const { user } = useAuth();
  const [reps, setReps] = useState<Rep[]>([]);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [memberships, setMemberships] = useState<Set<string>>(new Set());
  const [creators, setCreators] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const { data: repData } = await supabase
        .from("fan_reputation")
        .select("*")
        .eq("fan_id", user.id)
        .order("loyalty_score", { ascending: false });
      const list = (repData as any[] as Rep[]) || [];
      setReps(list);

      const creatorIds = Array.from(new Set(list.map((r) => r.creator_id)));
      if (creatorIds.length) {
        const [{ data: profs }, { data: circleData }, { data: mems }] = await Promise.all([
          supabase.from("profiles").select("user_id, display_name, cashtag, avatar_url").in("user_id", creatorIds),
          supabase.from("creator_circles").select("*").in("creator_id", creatorIds),
          supabase.from("circle_members").select("circle_id").eq("fan_id", user.id),
        ]);
        const map: Record<string, Profile> = {};
        (profs as any[] | null)?.forEach((p) => { map[p.user_id] = p; });
        setCreators(map);
        setCircles((circleData as any[] as Circle[]) || []);
        setMemberships(new Set(((mems as any[] | null) || []).map((m) => m.circle_id)));
      } else {
        setCircles([]);
        setMemberships(new Set());
        setCreators({});
      }
      setLoading(false);
    })();
  }, [user]);

  const circlesByCreator = useMemo(() => {
    const m: Record<string, Circle[]> = {};
    for (const c of circles) {
      (m[c.creator_id] ||= []).push(c);
    }
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) => (a.min_support_cents + a.min_loyalty_score) - (b.min_support_cents + b.min_loyalty_score))
    );
    return m;
  }, [circles]);

  const totals = useMemo(() => {
    return reps.reduce(
      (acc, r) => ({
        support: acc.support + (r.support_total_cents || 0),
        loyalty: acc.loyalty + (r.loyalty_score || 0),
        missions: acc.missions + (r.missions_completed || 0),
      }),
      { support: 0, loyalty: 0, missions: 0 }
    );
  }, [reps]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost">
            <Link to="/wallet"><ArrowLeft className="size-5" /></Link>
          </Button>
          <h1 className="text-xl font-bold">My Reputation</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="size-4 text-primary" /> Lifetime support
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <Stat icon={<Heart className="size-4" />} label="Supported" value={fmtUSD(totals.support)} />
            <Stat icon={<Trophy className="size-4" />} label="Loyalty" value={totals.loyalty.toLocaleString()} />
            <Stat icon={<Target className="size-4" />} label="Missions" value={totals.missions.toLocaleString()} />
          </CardContent>
        </Card>

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-10">Loading reputation…</p>
        ) : reps.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                You haven't built reputation with any creator yet. Send support, join missions, or scan QR hunts to start.
              </p>
              <Button asChild size="sm"><Link to="/discover">Discover creators</Link></Button>
            </CardContent>
          </Card>
        ) : (
          reps.map((r) => {
            const creator = creators[r.creator_id];
            const list = circlesByCreator[r.creator_id] || [];
            return (
              <Card key={r.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">
                        {creator?.display_name || "Creator"}
                      </CardTitle>
                      {creator?.cashtag && (
                        <Link to={`/u/${creator.cashtag}`} className="text-xs text-muted-foreground hover:underline">
                          ${creator.cashtag}
                        </Link>
                      )}
                    </div>
                    <Badge variant="secondary" className="shrink-0">
                      Loyalty {r.loyalty_score}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <MiniStat label="Supported" value={fmtUSD(r.support_total_cents)} />
                    <MiniStat label="Loyalty" value={String(r.loyalty_score)} />
                    <MiniStat label="Missions" value={String(r.missions_completed)} />
                  </div>

                  {list.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tier gating</p>
                      {list.map((c) => {
                        const supportPct =
                          c.min_support_cents > 0
                            ? Math.min(100, (r.support_total_cents / c.min_support_cents) * 100)
                            : 100;
                        const loyaltyPct =
                          c.min_loyalty_score > 0
                            ? Math.min(100, (r.loyalty_score / c.min_loyalty_score) * 100)
                            : 100;
                        const unlocked =
                          r.support_total_cents >= c.min_support_cents &&
                          r.loyalty_score >= c.min_loyalty_score;
                        const joined = memberships.has(c.id);
                        return (
                          <div key={c.id} className="rounded-lg border p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {joined ? (
                                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                                ) : unlocked ? (
                                  <Sparkles className="size-4 text-primary shrink-0" />
                                ) : (
                                  <Lock className="size-4 text-muted-foreground shrink-0" />
                                )}
                                <span className="text-sm font-medium truncate">{c.name}</span>
                              </div>
                              <Badge variant={joined ? "default" : unlocked ? "secondary" : "outline"} className="shrink-0">
                                {joined ? "Member" : unlocked ? "Unlocked" : "Locked"}
                              </Badge>
                            </div>

                            {c.min_support_cents > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Support</span>
                                  <span>{fmtUSD(r.support_total_cents)} / {fmtUSD(c.min_support_cents)}</span>
                                </div>
                                <Progress value={supportPct} className="h-1.5" />
                              </div>
                            )}
                            {c.min_loyalty_score > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Loyalty</span>
                                  <span>{r.loyalty_score} / {c.min_loyalty_score}</span>
                                </div>
                                <Progress value={loyaltyPct} className="h-1.5" />
                              </div>
                            )}

                            {!unlocked && (
                              <p className="text-xs text-muted-foreground">
                                {r.support_total_cents < c.min_support_cents
                                  ? `Send ${fmtUSD(c.min_support_cents - r.support_total_cents)} more to unlock.`
                                  : `Earn ${c.min_loyalty_score - r.loyalty_score} more loyalty to unlock.`}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {Array.isArray(r.badges) && r.badges.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {r.badges.map((b: any, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(b)}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </main>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="text-center space-y-1">
      <div className="flex items-center justify-center text-primary">{icon}</div>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/40 py-2">
      <div className="text-sm font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
